import React from 'react';
import { render, screen, fireEvent, waitFor } from '@testing-library/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CandidateTags from '../CandidateTags';
import Api from '../API';

jest.mock('../API');
const mockApi = Api as jest.Mocked<typeof Api>;

const createTestQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: false
      }
    }
  });

const theme = createTheme();

const renderWithProviders = (component: React.ReactElement) => {
  const queryClient = createTestQueryClient();
  return render(
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>{component}</ThemeProvider>
    </QueryClientProvider>
  );
};

const mockCandidate = {
  id: 1,
  firstname: 'John',
  lastname: 'Doe',
  email: 'john@example.com',
  tag_list: ['tag1', 'tag2'],
  entity_tags: [
    { value: 'tag1', label: 'Tag 1' },
    { value: 'tag2', label: 'Tag 2' },
    { value: 'tag3', label: 'Tag 3' }
  ]
};

describe('CandidateTags', () => {
  const mockDispatch = jest.fn();

  beforeEach(() => {
    jest.clearAllMocks();
    mockApi.updateCandidateTags.mockResolvedValue({ res: mockCandidate });
  });

  it('renders candidate tags section', () => {
    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    expect(screen.getByText('Candidate Tags')).toBeInTheDocument();
  });

  it('displays existing tags', () => {
    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    expect(screen.getByText('tag1')).toBeInTheDocument();
    expect(screen.getByText('tag2')).toBeInTheDocument();
  });

  it('allows adding new tags', async () => {
    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    fireEvent.change(input, { target: { value: 'newtag' } });
    fireEvent.keyDown(input, { key: 'Enter', code: 'Enter' });

    await waitFor(() => {
      expect(mockApi.updateCandidateTags).toHaveBeenCalled();
    });
  });

  it('calls updateCandidateTags with correct parameters', async () => {
    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    fireEvent.change(input, { target: { value: 'tag3' } });
    fireEvent.keyDown(input, { key: 'Enter', code: 'Enter' });

    await waitFor(() => {
      expect(mockApi.updateCandidateTags).toHaveBeenCalledWith(1, expect.arrayContaining(['tag3']));
    });
  });

  it('handles tag update success', async () => {
    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    fireEvent.change(input, { target: { value: 'newtag' } });
    fireEvent.keyDown(input, { key: 'Enter', code: 'Enter' });

    await waitFor(() => {
      expect(mockDispatch).toHaveBeenCalledWith({
        type: 'SET_SNACKBAR',
        payload: {
          message: 'Candidate tags have been successfully updated',
          state: 'success'
        }
      });
    });
  });

  it('handles tag update error', async () => {
    mockApi.updateCandidateTags.mockRejectedValue({ error: 'Update failed' });

    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    fireEvent.change(input, { target: { value: 'newtag' } });
    fireEvent.keyDown(input, { key: 'Enter', code: 'Enter' });

    await waitFor(() => {
      expect(mockDispatch).toHaveBeenCalledWith({
        type: 'SET_SNACKBAR',
        payload: {
          message: 'There was an error in updating candidate tags, Update failed',
          state: 'error'
        }
      });
    });
  });

  it('resets tags to original on error', async () => {
    mockApi.updateCandidateTags.mockRejectedValue({ error: 'Update failed' });

    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    fireEvent.change(input, { target: { value: 'newtag' } });
    fireEvent.keyDown(input, { key: 'Enter', code: 'Enter' });

    await waitFor(() => {
      expect(mockApi.updateCandidateTags).toHaveBeenCalled();
    });
  });

  it('sets focused state when tags change', async () => {
    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    fireEvent.change(input, { target: { value: 'newtag' } });
    fireEvent.keyDown(input, { key: 'Enter', code: 'Enter' });

    await waitFor(() => {
      expect(mockDispatch).toHaveBeenCalledWith({
        type: 'SET_FOCUSED',
        payload: true
      });
    });
  });

  it('sets focused to false on blur', () => {
    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    fireEvent.blur(input);

    expect(mockDispatch).toHaveBeenCalledWith({
      type: 'SET_FOCUSED',
      payload: false
    });
  });

  it('auto-focuses input when focused prop is true', () => {
    renderWithProviders(
      <CandidateTags focused={true} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    expect(input).toHaveFocus();
  });

  it('uses custom inputId when provided', () => {
    const { container } = renderWithProviders(
      <CandidateTags
        focused={false}
        candidate={mockCandidate}
        dispatch={mockDispatch}
        inputId="custom-tags-input"
      />
    );

    const input = container.querySelector('#custom-tags-input');
    expect(input).toBeInTheDocument();
  });

  it('filters options based on input', () => {
    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    fireEvent.change(input, { target: { value: 'tag' } });

    expect(input.value).toBe('tag');
  });

  it('suggests creating new tag when input does not match existing tags', () => {
    renderWithProviders(
      <CandidateTags focused={false} candidate={mockCandidate} dispatch={mockDispatch} />
    );

    const input = screen.getByPlaceholderText('Type your tag here...') as HTMLInputElement;
    fireEvent.change(input, { target: { value: 'completelynewtag' } });

    expect(input.value).toBe('completelynewtag');
  });
});
